import { useState, useEffect } from "react";
import ComfortProfileForm from "./comfort-profile-form";
import RecipientLanding from "./recipient-landing";
import ConversationPage from "./conversation-page";
import VoiceLanding from "./voice-landing";
import VoiceConversation from "./voice-conversation";
import OutputPage, { DEMO_PROMPT } from "./output-page";
import LandingPage from "./landing-page";
import FoundationPreview from "./components/world/FoundationPreview";
import { createGift, getGiftByLinkId } from "./supabase";
import Surface2 from "./surfaces/Surface2";
import Surface18 from "./surfaces/Surface18";
import IntroducerSavingSurface from "./surfaces/IntroducerSavingSurface";
import { ReceiverStatusSurface } from "./components/world/ReceiverPrimitives";
import SupportingStatusPage from "./components/world/SupportingStatusPage";
import PolicyPage, { getPolicyPage } from "./components/world/PolicyPage";
import useDecodedImage from "./hooks/useDecodedImage";
import { resolveGiftDestination } from "./gift-routing";
import {
  PurchaseAccessScreen,
  PurchaseRecoveryLoading,
  PurchaseRecoveryRequest,
} from "./purchase-recovery.jsx";
import {
  clearStoredPurchase,
  getDraftStorageKey,
  getPurchaseDestination,
  readStoredPurchase,
  writeStoredPurchase,
} from "./purchase-recovery";

const LINK_PARTICLES = [
  { left: "8%",  top: "55%", size: 1.5, opacity: 0.20, dur: "16s", delay: "0s",   travel: 420 },
  { left: "25%", top: "70%", size: 1.0, opacity: 0.18, dur: "19s", delay: "2.5s", travel: 580 },
  { left: "55%", top: "65%", size: 1.5, opacity: 0.22, dur: "14s", delay: "1.8s", travel: 350 },
  { left: "72%", top: "80%", size: 1.0, opacity: 0.15, dur: "17s", delay: "4.0s", travel: 500 },
  { left: "40%", top: "85%", size: 2.0, opacity: 0.20, dur: "12s", delay: "3.2s", travel: 680 },
  { left: "88%", top: "60%", size: 1.0, opacity: 0.18, dur: "15s", delay: "1.1s", travel: 420 },
];

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PREVIEW_DONE_LINK_ID = "00000000-0000-4000-8000-000000000069";
const MINIMUM_SAVING_DURATION_MS = 1500;
const AGE_GATE_BACKGROUND_URL = "/backgrounds/tgc-124/threshold-continuation-v1.png";
const DONE_BACKGROUND_URL = "/backgrounds/tgc-124/threshold-continuation-v1.png";
const RECEIVER_PREVIEW_PROFILE = {
  introducerName: "John",
  recipientName: "Sarah",
  theme: "warm",
  tech: "Uses it but doesn't love it",
  work: "Elementary school teacher",
  outside: "Gardening, baking sourdough, watercolor painting",
  personality: "Warm, a little self-deprecating, lights up when talking about her students",
  downplay: "Incredibly creative",
  lightup: "Talking about a student who finally got reading to click",
  avoid: "",
};
const OUTPUT_PREVIEW_SHORT_PROMPT = "PERSONAL AI PROMPT — SARAH\n\nTalk to me warmly and directly. Help me think through one thing at a time without overwhelming me with options.";
const OUTPUT_PREVIEW_LONG_PROMPT = [DEMO_PROMPT, DEMO_PROMPT, DEMO_PROMPT].join("\n\n—\n\n");

function getInitialDonePreview() {
  if (typeof window === "undefined") return null;

  const params = new URLSearchParams(window.location.search);
  if (params.get("preview") !== "done") return null;

  return {
    profile: { introducerName: "John", recipientName: "Sarah" },
    recipientLink: `${window.location.origin}/gift?id=${PREVIEW_DONE_LINK_ID}`,
  };
}

function readPurchaseDraft(giftCode) {
  const key = getDraftStorageKey(giftCode);
  if (!key) return null;
  try {
    const draft = JSON.parse(window.localStorage.getItem(key) || "null");
    return draft && typeof draft === "object" ? draft : null;
  } catch {
    return null;
  }
}

// APP FLOW:
// INTRODUCER: / -> checkout creates TGC Gift Code -> Stripe -> paid order -> local draft -> link ready
// RECOVERY:   Gift Code + checkout email -> one-time email link -> unfinished form or delivery screen
// RECIPIENT:  /gift?id=LINK_ID -> landing -> conversation -> output
// LEGACY:     /?gift=LINK_ID -> still works for old links

export default function App() {
  const urlParams = new URLSearchParams(window.location.search);
  const previewParam = urlParams.get("preview");
  const walkthroughMode = previewParam === "walk" || urlParams.get("walk") === "1";
  const imageryVariant = urlParams.get("imagery");
  const initialDonePreview = getInitialDonePreview();
  const [screen, setScreen] = useState(initialDonePreview ? "link-ready" : "loading");
  const [profile, setProfile] = useState(initialDonePreview?.profile ?? null);
  const [generatedPrompt, setGeneratedPrompt] = useState("");
  const [recipientLink, setRecipientLink] = useState(initialDonePreview?.recipientLink ?? "");
  const [giftLinkId, setGiftLinkId] = useState(null);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [promoCode, setPromoCode] = useState(null);
  const [purchase, setPurchase] = useState(null);
  const [purchaseAccessToken, setPurchaseAccessToken] = useState(null);
  const [purchaseInitialDraft, setPurchaseInitialDraft] = useState(null);
  const [purchaseEntry, setPurchaseEntry] = useState("recovery");
  const [savedPurchase, setSavedPurchase] = useState(() =>
    readStoredPurchase(window.localStorage)
  );
  const [voiceToken, setVoiceToken] = useState(null);
  const [voiceId, setVoiceId] = useState("eve");
  const [policyPage, setPolicyPage] = useState(null);
  const ageGateBackgroundReady = useDecodedImage(screen === "age-gate" ? AGE_GATE_BACKGROUND_URL : null);
  const shouldDecodeDoneBackground = profile && (screen === "saving" || screen === "link-ready");
  const doneBackgroundReady = useDecodedImage(shouldDecodeDoneBackground ? DONE_BACKGROUND_URL : null);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2500);
  };

  function applyPurchaseResponse(data, fallbackToken, entry) {
    const accessToken = data.accessToken || fallbackToken;
    const restoredPurchase = {
      ...data.purchase,
      recipientLink: data.purchase?.recipientLinkId
        ? `${window.location.origin}/gift?id=${data.purchase.recipientLinkId}`
        : null,
    };
    setPurchase(restoredPurchase);
    setPurchaseEntry(entry);

    if (accessToken) {
      setPurchaseAccessToken(accessToken);
      const stored = { giftCode: restoredPurchase.giftCode, accessToken };
      writeStoredPurchase(window.localStorage, stored);
      setSavedPurchase(stored);
    }

    const destination = getPurchaseDestination(restoredPurchase);
    if (destination === "delivery") {
      setProfile({ recipientName: restoredPurchase.recipientName });
      setRecipientLink(restoredPurchase.recipientLink);
      setScreen("link-ready");
      return;
    }
    if (destination === "unavailable") {
      setScreen("purchase-status");
      return;
    }
    if (destination === "continuation") {
      setPurchaseInitialDraft(readPurchaseDraft(restoredPurchase.giftCode));
      setScreen("purchase-ready");
      return;
    }
    setScreen("purchase-recovery");
  }

  async function confirmCheckout(sessionId) {
    setScreen("purchase-loading");
    try {
      const response = await fetch("/api/checkout-status", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ checkoutSessionId: sessionId }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "Purchase confirmation failed");
      window.history.replaceState({}, "", "/");
      applyPurchaseResponse(data, data.accessToken, "checkout");
    } catch (checkoutError) {
      setError(
        "We couldn't confirm this purchase automatically. Use your TGC Gift Code to recover it without paying again."
      );
      setScreen("purchase-recovery");
    }
  }

  async function restorePurchase({ accessToken, recoveryToken, entry = "recovery" }) {
    setScreen("purchase-loading");
    try {
      const response = await fetch("/api/restore-purchase", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          recoveryToken ? { recoveryToken } : { accessToken }
        ),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "Purchase recovery failed");
      if (recoveryToken) window.history.replaceState({}, "", "/");
      applyPurchaseResponse(data, accessToken, entry);
    } catch (recoveryError) {
      if (accessToken) {
        clearStoredPurchase(window.localStorage);
        setSavedPurchase(null);
      }
      setError(recoveryError.message || "This recovery link is invalid or expired.");
      setScreen("purchase-recovery");
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paid = params.get("paid");
    const preview = params.get("preview");

    if (process.env.NODE_ENV !== "production" && params.get("skip") === "Itshulapooper") {
      setProfile({ introducerName: "John", recipientName: "Sarah", theme: "warm" });
      setGeneratedPrompt("PERSONAL AI PROMPT — SARAH\n\nWHO I AM:\nWarm, self-deprecating, lights up when talking about the people she cares about. Communicates casually and openly once comfortable. Doesn't respond well to being told what to do — responds to curiosity and genuine interest.\n\nWHAT I'M BUILDING TOWARD:\nBringing creativity outside of the classroom — through art, writing, or something not yet named. Wants to do something just for herself.\n\nWHAT I'M WORKING THROUGH:\nTime and energy are tight. Comes home depleted. Needs help thinking through things without it feeling like homework.\n\nHOW TO ACTUALLY HELP ME:\n- Brainstorm creative projects that don't require a lot of startup energy\n- Help me write — I have things to say but get stuck getting started\n- Help me think through decisions without overwhelming me with options\n\nTHE TONE THAT WORKS:\nWarm, a little funny, zero pressure. No lists unless asked. One thing at a time.");
      setScreen("output");
      return;
    }

    if (preview === "landing") {
      setScreen("landing-preview");
      return;
    }

    if (preview === "walk") {
      setScreen("intro");
      return;
    }

    if (preview === "foundation") {
      setScreen("foundation-preview");
      return;
    }

    if (preview === "age-gate") {
      setScreen("age-gate");
      return;
    }

    if (preview === "saving") {
      setProfile({ introducerName: "John", recipientName: "Sarah", theme: "warm" });
      setScreen("saving");
      return;
    }

    if (["purchase-recovery", "purchase-ready", "purchase-draft", "purchase-refunded", "purchase-delivery"].includes(preview)) {
      const previewPurchase = {
        giftCode: "TGC-7M4K-P9Q2",
        status: preview === "purchase-refunded" ? "refunded" : "paid",
      };
      setPurchase(previewPurchase);
      setPurchaseEntry(preview === "purchase-ready" ? "checkout" : "recovery");
      if (preview === "purchase-recovery") {
        setScreen("purchase-recovery");
      } else if (preview === "purchase-delivery") {
        setPurchase({ ...previewPurchase, status: "gift_created" });
        setProfile({ recipientName: "Sarah" });
        setRecipientLink(`${window.location.origin}/gift?id=${PREVIEW_DONE_LINK_ID}`);
        setScreen("link-ready");
      } else if (preview === "purchase-refunded") {
        setScreen("purchase-status");
      } else {
        setPurchaseInitialDraft(
          preview === "purchase-draft"
            ? { phase: "questions", currentQ: 4, answers: { introducerName: "John", recipientName: "Sarah" }, inputVal: "" }
            : null
        );
        setScreen("purchase-ready");
      }
      return;
    }

    if (["receiver", "conversation", "voice-ready", "voice-active", "voice-speaking", "voice-muted", "voice-error", "voice-ending", "voice-generating"].includes(preview)) {
      setProfile(RECEIVER_PREVIEW_PROFILE);
      setGiftLinkId(PREVIEW_DONE_LINK_ID);
      if (preview === "receiver") setScreen("landing");
      else if (preview === "conversation") setScreen("conversation-preview");
      else if (preview === "voice-ready") setScreen("voice-landing");
      else {
        setVoiceToken("preview-only");
        setScreen(preview);
      }
      return;
    }

    if (["output", "output-short", "output-long", "output-email", "output-feedback", "output-feedback-success", "output-restored"].includes(preview)) {
      setProfile(RECEIVER_PREVIEW_PROFILE);
      setGiftLinkId(null);
      setGeneratedPrompt(
        preview === "output-short"
          ? OUTPUT_PREVIEW_SHORT_PROMPT
          : (preview === "output-long" ? OUTPUT_PREVIEW_LONG_PROMPT : DEMO_PROMPT)
      );
      setScreen(preview);
      return;
    }

    if (preview === "invalid") {
      setError("We couldn't find this gift. The link may be invalid or expired.");
      setScreen("error");
      return;
    }

    if (preview === "loading") {
      setScreen("loading");
      return;
    }

    if (preview === "done") {
      setProfile({ introducerName: "John", recipientName: "Sarah" });
      setRecipientLink(`${window.location.origin}/gift?id=${PREVIEW_DONE_LINK_ID}`);
      setScreen("link-ready");
      return;
    }

    if (window.location.pathname === "/recover") {
      const recoveryToken = params.get("token");
      if (recoveryToken) {
        restorePurchase({ recoveryToken, entry: "email" });
      } else {
        setScreen("purchase-recovery");
      }
      return;
    }

    const requestedPolicyPage = getPolicyPage(window.location.pathname);
    if (requestedPolicyPage) {
      setPolicyPage(requestedPolicyPage);
      setScreen("policy");
      return;
    }

    if (!["/", "/gift", "/recover"].includes(window.location.pathname)) {
      setScreen("unknown-route");
      return;
    }

    const promo = params.get("promo");
    if (promo) setPromoCode(promo);

    const sessionId = params.get("checkout_session_id");

    // Support new URL format (/gift?id=xxx) and legacy format (?gift=xxx)
    const linkId = window.location.pathname === "/gift"
      ? params.get("id")
      : params.get("gift");

    if (linkId) {
      setGiftLinkId(linkId);
      loadGift(linkId);
    } else if (paid === "true") {
      if (sessionId) confirmCheckout(sessionId);
      else setScreen("purchase-recovery");
    } else if (params.get("recover") === "1") {
      setScreen("purchase-recovery");
    } else {
      setScreen("intro");
    }
  // Initialization intentionally runs once against the arrival URL.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadGift = async (linkId) => {
    if (!UUID_REGEX.test(linkId)) {
      setError("We couldn't find this gift. The link may be invalid or expired.");
      setScreen("error");
      return;
    }

    try {
      const gift = await getGiftByLinkId(linkId);
      const destination = resolveGiftDestination(gift);

      if (destination.error) {
        setError(destination.error);
        setScreen("error");
        return;
      }

      setProfile(destination.profile);
      setGeneratedPrompt(destination.prompt);
      setScreen(destination.screen);
    } catch (err) {
      setError("We couldn't find this gift. The link may be invalid or expired.");
      setScreen("error");
    }
  };

  const handleFormComplete = async (answers) => {
    const savingStartedAt = Date.now();
    setProfile(answers);
    setScreen("saving");
    if (walkthroughMode) {
      const previewLink = new URL(window.location.origin);
      previewLink.searchParams.set("preview", "receiver");
      previewLink.searchParams.set("walk", "1");
      if (imageryVariant === "classic") previewLink.searchParams.set("imagery", "classic");
      window.setTimeout(() => {
        setRecipientLink(previewLink.toString());
        setScreen("link-ready");
      }, MINIMUM_SAVING_DURATION_MS);
      return;
    }
    if (!purchaseAccessToken) {
      setError("This recovered session is no longer available. Request a fresh recovery link and open it from the email.");
      setScreen("purchase-recovery");
      return;
    }
    try {
      const gift = await createGift(answers, purchaseAccessToken);
      const link = `${window.location.origin}/gift?id=${gift.recipient_link_id}`;
      const remainingSavingTime = Math.max(
        0,
        MINIMUM_SAVING_DURATION_MS - (Date.now() - savingStartedAt)
      );
      if (remainingSavingTime > 0) {
        await new Promise((resolve) => window.setTimeout(resolve, remainingSavingTime));
      }
      const draftKey = getDraftStorageKey(purchase?.giftCode);
      if (draftKey) window.localStorage.removeItem(draftKey);
      setPurchase((current) => current
        ? { ...current, status: "gift_created", recipientLink: link }
        : current
      );
      setPurchaseInitialDraft(null);
      setRecipientLink(link);
      setScreen("link-ready");
    } catch (err) {
      setError("Something went wrong saving your gift. Please try again.");
      setScreen("error");
    }
  };

  const handlePayment = async () => {
    setCheckoutLoading(true);
    try {
      const res = await fetch("/api/create-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(promoCode ? { promoCode } : {}),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setCheckoutLoading(false);
        setError("Something went wrong starting checkout. Please try again.");
        setScreen("error");
      }
    } catch (err) {
      setCheckoutLoading(false);
      setError("Something went wrong starting checkout. Please try again.");
      setScreen("error");
    }
  };

  const handleLandingCta = () => {
    if (walkthroughMode) {
      setScreen("age-gate");
      return;
    }
    if (window.sessionStorage.getItem("tgc_age_confirmed") === "true") {
      handlePayment();
      return;
    }
    setScreen("age-gate");
  };

  const handleAgeContinue = () => {
    if (walkthroughMode) {
      setScreen("form");
      return;
    }
    window.sessionStorage.setItem("tgc_age_confirmed", "true");
    handlePayment();
  };

  const handleBegin = () => setScreen(walkthroughMode ? "conversation-preview" : "conversation");

  const handleVoiceBegin = () => setScreen("voice-landing");

  const handleVoiceStart = (token, voice) => {
    setVoiceToken(token);
    if (voice) setVoiceId(voice);
    setScreen(walkthroughMode ? "voice-active" : "voice-conversation");
  };

  const handleVoiceComplete = (transcript) => {
    setGeneratedPrompt(transcript || "");
    setScreen("output");
  };

  const handleFallbackToText = () => setScreen("conversation");

  const handleConversationComplete = (prompt) => {
    setGeneratedPrompt(prompt);
    setScreen("output");
  };

  const handleMakeAnotherGift = () => {
    setProfile(null);
    setGeneratedPrompt("");
    setRecipientLink("");
    setGiftLinkId(null);
    setPurchase(null);
    setPurchaseAccessToken(null);
    setPurchaseInitialDraft(null);
    setScreen("intro");
  };

  const handlePurchaseRecovery = () => {
    setError("");
    if (savedPurchase?.accessToken) {
      restorePurchase({
        accessToken: savedPurchase.accessToken,
        entry: "same-device",
      });
      return;
    }
    setScreen("purchase-recovery");
  };

  const handleContinueDraft = () => {
    setPurchaseInitialDraft(readPurchaseDraft(purchase?.giftCode));
    setScreen("form");
  };

  const handleStartPurchaseOver = () => {
    const draftKey = getDraftStorageKey(purchase?.giftCode);
    if (draftKey) window.localStorage.removeItem(draftKey);
    setPurchaseInitialDraft(null);
    setScreen("form");
  };

  const handleRecoveryBack = () => {
    setError("");
    window.history.replaceState({}, "", "/");
    setScreen("intro");
  };

  const theme = (profile?.theme === "clean" || profile?.theme === "Clean & simple" || profile?.theme === "Straightforward & low-key") ? "clean" : "warm";

  const isIntroScreen = screen === "intro" || screen === "landing-preview" || screen === "foundation-preview";
  const isOutputScreen = screen.startsWith("output");
  const isPurchaseScreen = screen.startsWith("purchase");
  const isReadScreen = screen === "unknown-route" || screen === "policy";
  const isFullPageScrollScreen = isOutputScreen || isPurchaseScreen || screen === "link-ready" || isReadScreen;

  return (
    <div style={{
      height: (isIntroScreen || isFullPageScrollScreen) ? "auto" : "100vh",
      minHeight: isIntroScreen ? "100svh" : (isFullPageScrollScreen ? "100vh" : undefined),
      overflow: (isIntroScreen || isFullPageScrollScreen) ? "auto" : "hidden",
      WebkitOverflowScrolling: (isIntroScreen || isFullPageScrollScreen) ? "touch" : undefined,
      background: (isOutputScreen || isReadScreen) ? "var(--tgc-world-cream)" : ((isIntroScreen || screen === "link-ready") ? "#010d1e" : "#1a1714"),
    }}>
      <style>{`
        html, body { background: ${(isOutputScreen || isReadScreen) ? "var(--tgc-world-cream)" : "#1a1714"}; margin: 0; padding: 0; }
        @import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@600&display=swap');
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes bgFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes titleGlow {
          0%,100% { filter: drop-shadow(0 0 6px rgba(232,183,107,0.35)); }
          50%      { filter: drop-shadow(0 0 14px rgba(232,183,107,0.6)); }
        }
        @keyframes floatDust {
          0%   { transform: translateY(0) scale(1);   opacity: var(--op); }
          100% { transform: translateY(calc(var(--travel) * -1px)) scale(0.6); opacity: 0; }
        }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes linkShimmer {
          0%   { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes linkGlow {
          0%,100% { box-shadow: 0 0 8px rgba(232,183,107,0.2), 0 0 0px rgba(232,183,107,0); }
          50%     { box-shadow: 0 0 20px rgba(232,183,107,0.45), 0 0 40px rgba(232,183,107,0.15); }
        }
        .fade-up       { animation: fadeUp 0.7s ease forwards; }
        .app-bg        { background-image: url('/backgrounds/comfort-profile-mobile.jpg'); animation: bgFadeIn 0.8s ease forwards; }
        .app-title     { animation: titleGlow 3s 0.8s infinite; }
        @media (min-width: 768px) {
          .app-bg { background-image: url('/backgrounds/comfort-profile-desktop.jpg'); }
        }
        .link-card-glow { animation: linkGlow 2.5s 0.8s ease-in-out infinite; }
        .link-text-shimmer {
          background: linear-gradient(90deg, rgba(232,183,107,0.6) 0%, rgba(245,208,138,1) 40%, rgba(201,160,47,1) 50%, rgba(245,208,138,1) 60%, rgba(232,183,107,0.6) 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: linkShimmer 3s 1s linear infinite;
        }
      `}</style>

      {screen === "loading" && (
        <ReceiverStatusSurface heading="Loading..." />
      )}

      {screen === "saving" && (
        <div className="tgc-v2-gated-preview" data-v2-bg-ready={doneBackgroundReady ? "true" : "false"}>
          <div
            className={`tgc-v2-static-bg${doneBackgroundReady ? " tgc-v2-static-bg--ready" : ""}`}
            aria-hidden="true"
            style={{ "--tgc-v2-bg-image": `url("${DONE_BACKGROUND_URL}")` }}
          />
          <IntroducerSavingSurface
            recipientName={profile?.recipientName}
            theme={theme}
          />
        </div>
      )}

      {screen === "error" && (
        <ReceiverStatusSurface heading={error || "Something went wrong."} kind="error" />
      )}

      {screen === "unknown-route" && <SupportingStatusPage />}

      {screen === "policy" && policyPage && <PolicyPage page={policyPage} />}

      {screen === "purchase-loading" && <PurchaseRecoveryLoading />}

      {screen === "purchase-recovery" && (
        <PurchaseRecoveryRequest onBack={handleRecoveryBack} notice={error} />
      )}

      {(screen === "purchase-ready" || screen === "purchase-status") && purchase && (
        <PurchaseAccessScreen
          purchase={purchase}
          hasDraft={Boolean(purchaseInitialDraft)}
          isInitialCheckout={purchaseEntry === "checkout"}
          onContinue={handleContinueDraft}
          onStartOver={handleStartPurchaseOver}
          onBack={handleRecoveryBack}
        />
      )}

      {screen === "intro" && (
        <LandingPage
          onCreateGift={handleLandingCta}
          onRecoverPurchase={handlePurchaseRecovery}
          hasSavedPurchase={Boolean(savedPurchase)}
          isLoading={checkoutLoading}
        />
      )}

      {screen === "landing-preview" && (
        <LandingPage
          onCreateGift={handleLandingCta}
          onRecoverPurchase={handlePurchaseRecovery}
          hasSavedPurchase={Boolean(savedPurchase)}
          isLoading={checkoutLoading}
        />
      )}

      {screen === "foundation-preview" && (
        <FoundationPreview />
      )}

      {screen === "age-gate" && (
        <div className="tgc-v2-gated-preview" data-v2-bg-ready={ageGateBackgroundReady ? "true" : "false"} style={{ position: "relative", height: "100dvh", overflow: "hidden", background: "#010d1e" }}>
          <div
            className={`tgc-v2-static-bg${ageGateBackgroundReady ? " tgc-v2-static-bg--ready" : ""}`}
            aria-hidden="true"
            style={{ "--tgc-v2-bg-image": `url("${AGE_GATE_BACKGROUND_URL}")` }}
          />
          <Surface2
            onContinue={handleAgeContinue}
            onGoBack={() => setScreen("intro")}
          />
        </div>
      )}

      {screen === "link-ready" && profile && (
        <div className="tgc-v2-gated-preview" data-v2-bg-ready={doneBackgroundReady ? "true" : "false"} style={{ position: "relative", minHeight: "100dvh", overflow: "visible", background: "#010d1e" }}>
          <Surface18
            recipientName={profile?.recipientName || profile?.recipient_first_name}
            recipientLink={recipientLink}
            giftCode={purchase?.giftCode}
            previewRecipientHref={walkthroughMode ? recipientLink : null}
            onMakeAnother={handleMakeAnotherGift}
          />
        </div>
      )}

      {false && screen === "link-ready" && (
        <div style={{ position: "relative", height: "100dvh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* Background */}
          <div className="app-bg" style={{
            position: "absolute", inset: 0, zIndex: 0,
            backgroundSize: "cover", backgroundPosition: "center bottom",
          }} />
          <div style={{ position: "absolute", inset: 0, zIndex: 1, background: "rgba(20,16,12,0.52)" }} />

          {/* Dust particles */}
          {LINK_PARTICLES.map((p, i) => (
            <div key={i} style={{
              position: "absolute", left: p.left, top: p.top, zIndex: 1,
              width: p.size, height: p.size, borderRadius: "50%",
              background: `rgba(232,183,107,${p.opacity})`,
              animation: `floatDust ${p.dur} ${p.delay} infinite linear`,
              "--travel": p.travel, "--op": p.opacity,
              pointerEvents: "none",
            }} />
          ))}

          {/* Header */}
          <div style={{
            position: "relative", zIndex: 2, flexShrink: 0,
            padding: "12px 18px 10px",
            background: "rgba(26,23,20,0.85)", backdropFilter: "blur(10px)",
            borderBottom: "1px solid rgba(232,183,107,0.15)",
          }}>
            <div className="app-title" style={{
              fontFamily: "'Dancing Script', cursive", fontSize: 22, fontWeight: 600,
              background: "linear-gradient(180deg,#F5D08A,#C9A02F)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", display: "inline-block",
            }}>The Gift Conversation</div>
            <div style={{
              fontSize: 10, letterSpacing: 2, textTransform: "uppercase",
              color: "rgba(232,183,107,0.6)", marginTop: 2,
            }}>YOUR GIFT IS READY</div>
          </div>

          {/* Content */}
          <div style={{
            position: "relative", zIndex: 2, flex: 1,
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start",
            padding: "10vh 24px 32px", gap: 20,
          }}>
            {/* Hero heading */}
            <div style={{ animation: "fadeIn 0.6s ease forwards", textAlign: "center" }}>
              <div style={{
                fontFamily: "'Dancing Script', cursive", fontSize: 34, fontWeight: 600,
                background: "linear-gradient(180deg,#F5D08A,#C9A02F)",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                display: "inline-block", lineHeight: 1.4, marginBottom: 10,
              }}>Your gift is ready.</div>
              <div style={{ fontSize: 15, color: "#e8e0d4", lineHeight: 1.7, maxWidth: 300, margin: "0 auto" }}>
                Send this link to <span style={{
                  background: "linear-gradient(180deg,#F5D08A,#C9A02F)",
                  WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", display: "inline",
                  fontWeight: 600,
                }}>{profile?.recipientName}</span>. When they open it, their conversation will be waiting.
              </div>
            </div>

            {/* Link card */}
            <div className="link-card-glow" style={{
              animation: "fadeIn 0.7s 0.15s ease forwards", opacity: 0,
              background: "rgba(232,183,107,0.08)", border: "1px solid rgba(232,183,107,0.4)",
              borderRadius: 16, padding: "14px 18px",
              wordBreak: "break-all", width: "min(85vw, 420px)", textAlign: "center",
              lineHeight: 1.6,
            }}>
              <span className="link-text-shimmer" style={{ fontSize: 13 }}>
                {recipientLink}
              </span>
            </div>

            {/* Copy button */}
            <button
              onClick={() => {
                navigator.clipboard.writeText(recipientLink);
                showToast("Link copied!");
              }}
              style={{
                animation: "fadeIn 0.7s 0.3s ease forwards", opacity: 0,
                background: "linear-gradient(180deg,#F5D08A,#C9A02F)",
                color: "#1a1714", border: "none",
                padding: "14px 48px", borderRadius: 50,
                fontSize: 15, fontWeight: 700, cursor: "pointer",
                letterSpacing: 0.3,
              }}
            >
              Copy Link
            </button>

            {/* Sub-note */}
            <div style={{
              animation: "fadeIn 0.7s 0.45s ease forwards", opacity: 0,
              fontSize: 12, color: "#9a8e7e", textAlign: "center", lineHeight: 1.6,
            }}>
              You can also text or email it directly.
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div style={{
          position: "fixed",
          bottom: 40,
          left: "50%",
          transform: "translateX(-50%)",
          background: "linear-gradient(180deg,#F5D08A,#C9A02F)",
          color: "#1a1714",
          padding: "10px 20px",
          borderRadius: 8,
          fontSize: 14,
          fontWeight: 600,
          zIndex: 100,
          whiteSpace: "nowrap",
        }}>
          {toast}
        </div>
      )}

      {screen === "form" && (
        <ComfortProfileForm
          onComplete={handleFormComplete}
          draftStorageKey={getDraftStorageKey(purchase?.giftCode)}
          initialDraft={purchaseInitialDraft}
        />
      )}

      {screen === "landing" && profile && (
        <RecipientLanding
          introducerName={profile.introducerName}
          recipientName={profile.recipientName}
          theme={theme}
          onBegin={handleBegin}
          onVoiceBegin={handleVoiceBegin}
        />
      )}

      {screen === "voice-landing" && profile && giftLinkId && (
        <VoiceLanding
          profile={profile}
          giftId={giftLinkId}
          preview={walkthroughMode}
          onStartCall={handleVoiceStart}
          onFallbackToText={handleBegin}
        />
      )}

      {screen === "voice-conversation" && profile && voiceToken && (
        <VoiceConversation
          profile={profile}
          token={voiceToken}
          voice={voiceId}
          onComplete={handleVoiceComplete}
          onFallbackToText={handleFallbackToText}
        />
      )}

      {screen.startsWith("voice-") && screen !== "voice-landing" && screen !== "voice-conversation" && profile && voiceToken && (
        <VoiceConversation
          profile={profile}
          token={voiceToken}
          voice="eve"
          previewState={screen === "voice-speaking" ? "active" : screen.replace("voice-", "")}
          previewSpeaking={screen === "voice-speaking"}
          previewError={screen === "voice-error" ? "Connection failed. Please try the text conversation instead." : ""}
          onComplete={() => {
            setGeneratedPrompt(DEMO_PROMPT);
            setScreen("output");
          }}
          onFallbackToText={() => setScreen("conversation-preview")}
        />
      )}

      {(screen === "conversation" || screen === "conversation-preview") && profile && (
        <ConversationPage
          profile={profile}
          preview={screen === "conversation-preview"}
          previewPrompt={DEMO_PROMPT}
          onComplete={handleConversationComplete}
        />
      )}

      {screen.startsWith("output") && profile && (
        <OutputPage
          recipientName={profile.recipientName}
          introducerName={profile.introducerName}
          theme={theme}
          prompt={generatedPrompt}
          giftLinkId={walkthroughMode || screen === "output-restored" ? null : giftLinkId}
          previewState={screen === "output" || screen === "output-short" || screen === "output-long" || screen === "output-restored" ? null : screen.replace("output-", "")}
          readOnly={screen === "output-restored"}
        />
      )}
    </div>
  );
}
